import React, { useState, useRef, useEffect } from 'react';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, PaperPlaneRight, Sparkle, Quotes, ChatTeardropDots, X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_QUESTIONS = [
  'Cos\'è il PCV (Prezzo Commercializzazione Vendita)?',
  'Come si calcola la componente UC1 per BTA3?',
  'Quali sono i tempi di switching standard ARERA?',
  'Cosa significa "tipo lettura S" in fattura?',
  'Differenza tra ASOS e ARIM?',
  'Come si applica l\'accisa per uso domestico?',
];

export default function EspertoArera() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const q = (text || input || '').trim();
    if (!q || loading) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text: q }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/knowledge/ask`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, session_id: sessionId }),
      });
      const d = await res.json();
      setSessionId(d.session_id);
      setMessages(m => [...m, {
        role: 'assistant', text: d.answer || '—',
        citations: d.citations || [], no_sources: d.no_sources,
      }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: 'Errore di connessione.', error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" data-testid="esperto-arera-btn"
                className="border-orange-200 text-orange-600 hover:bg-orange-50">
          <BookOpen size={16} weight="duotone" className="mr-1.5" />
          Esperto ARERA
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="border-b px-4 py-3 flex flex-row items-center justify-between space-y-0">
          <SheetTitle className="flex items-center gap-2">
            <BookOpen size={20} weight="duotone" className="text-orange-500" />
            Esperto ARERA
            <Badge variant="outline" className="text-[10px]">Gemini Flash + RAG</Badge>
          </SheetTitle>
          {messages.length > 0 && (
            <Button size="sm" variant="ghost" onClick={newChat} data-testid="new-chat-btn">
              Nuova chat
            </Button>
          )}
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
          {messages.length === 0 && (
            <div className="text-center pt-8">
              <Sparkle size={36} weight="duotone" className="mx-auto text-orange-400 mb-2" />
              <p className="text-sm text-slate-600 font-medium">Chiedi qualunque cosa sulla normativa ARERA</p>
              <p className="text-xs text-slate-400 mt-1 mb-4">L'AI cita gli articoli dei Testi Integrati indicizzati.</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                          data-testid={`suggested-q-${i}`}
                          className="text-xs px-3 py-1.5 bg-white border rounded-full hover:border-orange-300 hover:text-orange-600 text-slate-600">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <Message key={i} m={m} />
          ))}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 italic px-3">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce [animation-delay:0.1s]"></div>
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
              L'esperto sta consultando i Testi Integrati...
            </div>
          )}
        </div>

        <div className="border-t p-3 bg-white">
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
                   placeholder="Es. Cos'è la componente UC3?"
                   disabled={loading}
                   data-testid="esperto-input" />
            <Button type="submit" disabled={loading || !input.trim()}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="esperto-send-btn">
              <PaperPlaneRight size={16} />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Message({ m }) {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-orange-500 text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
          {m.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%]">
        <div className={`rounded-2xl rounded-tl-sm px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
          m.error ? 'bg-red-50 text-red-700' : 'bg-white border border-slate-200'
        }`}>
          {m.no_sources && (
            <div className="text-xs text-yellow-700 mb-2 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 inline-block">
              ⚠ Knowledge base vuota
            </div>
          )}
          {m.text}
        </div>
        {(m.citations || []).length > 0 && (
          <div className="mt-1.5 space-y-1">
            <div className="text-[10px] text-slate-400 flex items-center gap-1">
              <Quotes size={10} /> Fonti citate:
            </div>
            {m.citations.slice(0, 5).map(c => (
              <div key={c.n} className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                <span className="font-mono text-orange-600 mr-1">[{c.n}]</span>
                <span className="font-medium">{c.label}</span>
                <span className="text-slate-500"> — pag. {c.page}</span>
                {c.preview && (
                  <div className="text-[10px] text-slate-500 mt-0.5 italic line-clamp-2">
                    "{c.preview}..."
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
